import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { RefreshCw } from "lucide-react";
import Swal from "sweetalert2";
const base_url = process.env.REACT_APP_API_URL;

const AdminManageMatkaList = () => {
  const navigate = useNavigate();
  const auth = localStorage.getItem("admin_token");
  const [multiplier, setMultiplier] = useState("");
  const [showMultiplier, setShowMultiplier] = useState(false);

  const [isSidebarOpen, setIsSidebarOpen] = useState(
    !(window.innerWidth < 768),
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [markets, setMarkets] = useState([]);
  const [marketName, setMarketName] = useState("");
  const [openTime, setOpenTime] = useState("");
  const [closeTime, setCloseTime] = useState("");
  const [resultTime, setResultTime] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchMarkets = async () => {
    try {
      const res = await axios.get(`${base_url}/api/admin/matka/markets`, {
        headers: { Authorization: auth },
      });
      if (res.data.success) setMarkets(res.data.markets);
    } catch {
      toast.error("Failed to load markets");
    }
  };

  const fetchMultiplier = async () => {
    const res = await axios.get(`${base_url}/api/admin/matka/settings`, {
      headers: { Authorization: auth },
    });

    if (res.data.success) {
      setMultiplier(res.data.multiplier);
    }
  };

  useEffect(() => {
    fetchMarkets();
    fetchMultiplier();
    fetchMarkets();
  }, []);

  const addMarket = async () => {
    if (!marketName || !openTime || !closeTime || !resultTime) {
      toast.error("All fields required");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${base_url}/api/admin/matka/market/add`,
        { marketName, openTime, closeTime, resultTime },
        { headers: { Authorization: auth } },
      );

      if (res.data.success) {
        toast.success(res.data.message || "Market added");
        setMarketName("");
        setOpenTime("");
        setCloseTime("");
        setResultTime("");
        fetchMarkets();
      } else {
        // ✅ SHOW BACKEND ERROR MESSAGE
        toast.error(res.data.message || "Failed to add market");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Server error while adding market",
      );
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {

    if(status === 0){
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You are about to close this market.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, close it!",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) return;
    }

    try {
      await axios.post(
        `${base_url}/api/admin/matka/market/status`,
        { market_id: id, status },
        { headers: { Authorization: auth } },
      );
      fetchMarkets();
    } catch {
      toast.error("Status update failed");
    }
  };

  const deleteMarket = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;
    try {
      await axios.post(
        `${base_url}/api/admin/matka/market/delete`,
        { market_id: id },
        { headers: { Authorization: auth } },
      );
      fetchMarkets();
      toast.success("Deleted Successfully!");
    } catch {
      toast.error("Delete update failed");
    }
  };

  const saveMultiplier = async () => {
    try {
      await axios.post(
        `${base_url}/api/admin/matka/settings/update`,
        { multiplier },
        { headers: { Authorization: auth } },
      );
      toast.success("Multiplier updated successfully");
    } catch {
      toast.error("Failed to update multiplier");
    }
  };

  const formatTimeAMPM = (time) => {
    if (!time) return "";

    const [hour, minute] = time.split(":");
    let h = parseInt(hour, 10);
    const ampm = h >= 12 ? "PM" : "AM";

    h = h % 12 || 12; // convert 0 → 12
    return `${h}:${minute} ${ampm}`;
  };

  return (
    <div className="wrapper">

      <div
        className={`content-wrapper ${
          !isMobile && isSidebarOpen ? "" : "open-content-wrapper"
        }`}
      >
        <div className="page-content p-3">
          {/* ADD MARKET */}
          <div className="card mb-4">
            <div className="card-header">
              <h5>Add Matka Market</h5>
            </div>
            <div className="card-body row g-3">
              <div className="col-md-4">
                <label className="form-label fw-bold">Market Name</label>
                <input
                  className="form-control"
                  placeholder="Market Name"
                  value={marketName}
                  onChange={(e) => setMarketName(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label fw-bold">Open Time</label>
                <input
                  type="time"
                  className="form-control"
                  value={openTime}
                  onChange={(e) => setOpenTime(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label fw-bold">Close Time</label>
                <input
                  type="time"
                  className="form-control"
                  value={closeTime}
                  onChange={(e) => setCloseTime(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label fw-bold">Result Time</label>
                <input
                  type="time"
                  className="form-control"
                  value={resultTime}
                  onChange={(e) => setResultTime(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <button
                  className="btn btn-success w-100 mt-4"
                  onClick={addMarket}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Add"}
                </button>
              </div>
            </div>
          </div>

          {showMultiplier && (
            <div className="card-body border-bottom d-flex align-items-center gap-2">
              <label className="fw-bold mb-0">Winning Multiplier</label>

              <input
                type="number"
                step="0.1"
                className="form-control w-25"
                value={multiplier}
                onChange={(e) => setMultiplier(e.target.value)}
                placeholder="e.g. 9"
              />

              <span className="fw-bold">×</span>

              <button
                className="btn btn-success btn-sm"
                onClick={saveMultiplier}
              >
                Save
              </button>
            </div>
          )}

          {/* MARKET LIST */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Matka Markets</h5>

              <div className="d-flex gap-2">
                {/* <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowMultiplier(!showMultiplier)}
                >
                  {showMultiplier ? "Hide Multiplier" : "Set Multiplier"}
                </button> */}

                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={fetchMarkets}
                >
                  <RefreshCw size={14} /> Refresh
                </button>
              </div>
            </div>

            <div className="card-body table-responsive p-0">
              <table className="table table-bordered mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Market</th>
                    <th>Open</th>
                    <th>Close</th>
                    <th>Result Time</th>
                    <th>Result</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {markets.map((m, i) => (
                    <tr key={m.id}>
                      <td>{i + 1}</td>
                      <td>{m.market_name}</td>
                      <td>{formatTimeAMPM(m.open_time)}</td>
                      <td>{formatTimeAMPM(m.close_time)}</td>
                      <td>{formatTimeAMPM(m.result_time)}</td>
                      <td>{m.result ?? "No Result Yet"}</td>
                      <td>
                        <span
                          className={`badge ${
                            m.status ? "bg-success" : "bg-danger"
                          }`}
                        >
                          {m.status ? "OPEN" : "CLOSED"}
                        </span>
                      </td>
                      <td className="d-flex gap-1">
                        {/* OPEN */}
                        {!m.result && m.status !== 1 && (
                          <button
                            className="btn btn-sm btn-success m-0"
                            onClick={() => updateStatus(m.id, 1)}
                            title="Open market"
                          >
                            Open
                          </button>
                        )}

                        {/* CLOSE */}
                        {m.status === 1 && (
                          <button
                            className="btn btn-sm btn-danger m-0"
                            onClick={() => updateStatus(m.id, 0)}
                          >
                            Close
                          </button>
                        )}

                        {/* MANAGE */}
                        <button
                          className="btn btn-sm btn-primary m-0"
                          onClick={() =>
                            navigate(`/admin/adminManageMatka/${m.id}`)
                          }
                        >
                          Manage
                        </button>

                        <button
                          className="btn btn-sm btn-danger m-0"
                          onClick={() => deleteMarket(m.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!markets.length && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        No markets
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminManageMatkaList;
